import { inject, Inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { VehicleProducersModel } from '../models/VehicleProducersModel';
import { SortDirectionValuesModel } from '../models/SortDirectionValues-model';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from './Notification.service';
import { DeleteDialogComponent } from '../helpers/delete-dialog/delete-dialog.component';
import { forkJoin } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class VehicleProducersService {
    private url = `${this.API_URL}`;

    public vehicleProducers = signal<VehicleProducersModel[]>([]);
    public producerIndex = signal<number | null>(null);
    public editInputRow = signal(false);
    public newInputRow = signal<VehicleProducersModel>({} as VehicleProducersModel);
    public isLoading = signal<boolean>(false);
    public isAddingNewProducer = signal<boolean>(false);
    public notificationService = inject(NotificationService);
    dialog = inject(MatDialog);

    public pageNumber = signal<number>(0);
    public pageSize = signal<number>(50);
    public sortDirectionValues = signal<SortDirectionValuesModel>({active: "id", direction: "asc"});

    public correctSearchTerm = signal<string>('');
    public acceptedSearchTerm = signal<string>('');
    public isSearchMode = signal<boolean>(false);
    public searchResults = signal<VehicleProducersModel[]>([]);

    constructor(@Inject('API_URL') private API_URL: string, private httpClient: HttpClient) {
    }

    triggerAllVehicleProducers() {
        return this.httpClient.get<{ content: VehicleProducersModel[] }>(`${this.url}/vehicle-producer`, {
            params: {
                pageNumber: this.pageNumber(),
                pageSize: this.pageSize(),
                sortAttribute: this.sortDirectionValues().active,
                sortDirection: this.sortDirectionValues().direction
            }
        });
    }

    triggerSearchVehicleProducers(term: string, type: 'CORRECT' | 'ACCEPTED') {
        return this.httpClient.get<VehicleProducersModel[]>(`${this.url}/vehicle-producer/search`, {
            params: {
                term,
                type
            }
        });
    }

    applySearchFilters(correctTerm: string, acceptedTerm: string) {
        const trimmedCorrectTerm = correctTerm.trim();
        const trimmedAcceptedTerm = acceptedTerm.trim();
        const hasCorrectTerm = trimmedCorrectTerm.length >= 3;
        const hasAcceptedTerm = trimmedAcceptedTerm.length >= 3;

        this.correctSearchTerm.set(trimmedCorrectTerm);
        this.acceptedSearchTerm.set(trimmedAcceptedTerm);

        if (!hasCorrectTerm && !hasAcceptedTerm) {
            this.exitSearchMode();
            return;
        }

        this.isSearchMode.set(true);
        this.isLoading.set(true);

        if (hasCorrectTerm && hasAcceptedTerm) {
            forkJoin({
                correct: this.triggerSearchVehicleProducers(trimmedCorrectTerm, 'CORRECT'),
                accepted: this.triggerSearchVehicleProducers(trimmedAcceptedTerm, 'ACCEPTED')
            }).subscribe({
                next: ({correct, accepted}) => {
                    const acceptedIds = new Set(accepted.map((producer) => producer.id));
                    const intersection = correct.filter((producer) => acceptedIds.has(producer.id));
                    this.searchResults.set(intersection);
                    this.isLoading.set(false);
                },
                error: (error) => {
                    this.isLoading.set(false);
                    this.notificationService.showErrorMessage("Error searching vehicle producers", "Error");
                    console.error('Search error:', error);
                }
            });
            return;
        }

        if (hasCorrectTerm) {
            this.triggerSearchVehicleProducers(trimmedCorrectTerm, 'CORRECT').subscribe({
                next: (response) => {
                    this.searchResults.set(response);
                    this.isLoading.set(false);
                },
                error: (error) => {
                    this.isLoading.set(false);
                    this.notificationService.showErrorMessage("Error searching vehicle producers", "Error");
                    console.error('Search error:', error);
                }
            });
            return;
        }

        this.triggerSearchVehicleProducers(trimmedAcceptedTerm, 'ACCEPTED').subscribe({
            next: (response) => {
                this.searchResults.set(response);
                this.isLoading.set(false);
            },
            error: (error) => {
                this.isLoading.set(false);
                this.notificationService.showErrorMessage("Error searching vehicle producers", "Error");
                console.error('Search error:', error);
            }
        });
    }

    private exitSearchMode() {
        this.isSearchMode.set(false);
        this.searchResults.set([]);
        this.isLoading.set(false);
    }

    clearSearch() {
        this.correctSearchTerm.set('');
        this.acceptedSearchTerm.set('');
        this.isSearchMode.set(false);
        this.searchResults.set([]);
        this.getVehicleProducersTable();
    }

    checkDuplicateProducer(name: string): boolean {
        const lowerName = name.trim().toLowerCase();
        return this.vehicleProducers().some(p => p.correctVehicleProducerName.trim().toLowerCase() === lowerName);
    }


    getCurrentProducers(): VehicleProducersModel[] {
        return this.isSearchMode() ? this.searchResults() : this.vehicleProducers();
    }

    getVehicleProducersTable() {
        this.isLoading.set(true);
        this.triggerAllVehicleProducers().subscribe({
            next: (response) => {
                this.vehicleProducers.set(response.content);
                this.isLoading.set(false);
            },
            error: (error) => {
                this.isLoading.set(false);
                this.notificationService.showErrorMessage("Error loading vehicle producers", "Error");
            }
        });
    }

    getVehicleProducersOnScroll() {
        console.log('getVehicleProducersOnScroll called for page:', this.pageNumber());

        this.triggerAllVehicleProducers().subscribe({
            next: (response) => {
                this.vehicleProducers.update((producers) => [...producers, ...response.content]);

                this.isLoading.set(false);
            },
            error: (error) => {
                console.error('Error in getVehicleProducersOnScroll:', error);
                this.isLoading.set(false);
                this.notificationService.showErrorMessage("Error loading more vehicle producers", "Error");
            }
        });
    }

    onScroll() {
        if (this.isLoading()) {
            console.log('Already loading, skipping...');
            return;
        }

        if (this.isSearchMode()) {
            return;
        }
        console.log('Loading page:', this.pageNumber() + 1);
        this.isLoading.set(true);
        this.increasePageNumber();
        this.getVehicleProducersOnScroll();
    }

    increasePageNumber() {
        this.pageNumber.update((prevNum) => prevNum + 1);
    }

    changeSortDirection(sortDirection: SortDirectionValuesModel, tableElement?: HTMLElement) {
        try {
            this.pageNumber.set(0);
            this.sortDirectionValues.set(sortDirection);
            if (tableElement) {
                tableElement.scrollTop = 0;
            }

            this.triggerAllVehicleProducers().subscribe({
                next: (response) => {
                    if (response) {
                        this.vehicleProducers.set([...response.content]);
                    }
                },
                error: (error) => {
                    if (error.status === 409) {
                        this.notificationService.showErrorMessage("This attribute does not exist in this table", "Error");
                    } else {
                        this.notificationService.showErrorMessage("Something went wrong", "Error Occurred");
                    }
                    this.resetSortDirection();
                }
            });
        } catch (error) {
            this.notificationService.showErrorMessage("Error occurred while sorting", "Error");
        }
    }

    resetSortDirection() {
        this.sortDirectionValues.set({active: "id", direction: "asc"});
    }

    resetPageNumber() {
        this.pageNumber.set(0);
    }

    onRefreshTable() {
        this.pageNumber.set(0);
        this.pageSize.set(50);
        this.sortDirectionValues.set({active: "id", direction: "asc"});
        this.vehicleProducers.set([]);
        this.editInputRow.set(false);
        this.producerIndex.set(null);
        this.isAddingNewProducer.set(false);
        this.resetNewInputRow();
        this.clearSearch();
        this.getVehicleProducersTable();
    }

    triggerOnScroll() {
        this.increasePageNumber();
        this.triggerAllVehicleProducers().subscribe(response => {
            this.vehicleProducers.update((producers) => [...producers, ...response.content]);
        });
    }

    addVehicleProducer() {
        this.isAddingNewProducer.set(true);
        this.httpClient.post<VehicleProducersModel>(`${this.url}/vehicle-producer`, {
            correctVehicleProducerName: this.newInputRow().correctVehicleProducerName,
            acceptedVehicleProducerName: this.newInputRow().acceptedVehicleProducerName,
        }, {
            observe: "response"
        }).subscribe({
            next: response => {
                if (response.status === 201) {
                    this.onRefreshTable();
                    this.isAddingNewProducer.set(false);
                    this.notificationService.showSuccessMessage("Successfully added", 'Successfully added');
                }
                this.resetNewInputRow();
                this.editInputRow.set(false);
            },
            error: err => {
                this.isAddingNewProducer.set(false);
                this.handleError(err);
            }
        });
    }

    resetNewInputRow() {
        this.newInputRow.set({} as VehicleProducersModel);
    }

    openNewRow() {
        this.editInputRow.set(!this.editInputRow());
        this.resetNewInputRow();
    }

    resetComponentState() {
        this.editInputRow.set(false);
        this.producerIndex.set(null);
        this.resetNewInputRow();
        this.isAddingNewProducer.set(false);
        this.clearSearch();
    }

    updateVehicleProducer(newProducer: VehicleProducersModel) {
        this.isAddingNewProducer.set(true);
        this.httpClient.put<VehicleProducersModel>(`${this.url}/vehicle-producer/${newProducer.id}`, {
            correctVehicleProducerName: newProducer.correctVehicleProducerName,
            acceptedVehicleProducerName: newProducer.acceptedVehicleProducerName
        }, {
            observe: "response"
        }).subscribe({
            next: response => {
                if (response.status === 200) {
                    this.vehicleProducers.update((producers) =>
                        producers.map((prevProducer) =>
                            prevProducer.id === newProducer.id ? response.body! : prevProducer
                        )
                    );
                    if (this.isSearchMode()) {
                        this.searchResults.update((producers) =>
                            producers.map((prevProducer) =>
                                prevProducer.id === newProducer.id ? response.body! : prevProducer
                            )
                        );
                    }
                    this.notificationService.showSuccessMessage("Successfully updated", "Successfully updated");
                    this.producerIndex.set(null);
                }
                this.isAddingNewProducer.set(false);
            },
            error: err => {
                this.isAddingNewProducer.set(false);
                this.handleError(err);
            }
        });
    }

    deleteVehicleProducer(producer: VehicleProducersModel) {
        const dialogRef = this.dialog.open(DeleteDialogComponent, {
            width: "350px",
            data: "Are you sure you want to delete this vehicle producer?"
        });
        dialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
                this.deleteVehicleProducerRequest(producer.id);
            }
        });
    }

    deleteVehicleProducerRequest(id: number) {
        this.httpClient.delete(`${this.url}/vehicle-producer/${id}`, {observe: "response"}).subscribe({
            next: response => {
                if (response.status === 204) {
                    this.vehicleProducers.update((producers) => producers.filter((producer) => producer.id !== id));

                    if (this.isSearchMode()) {
                        this.searchResults.update((producers) => producers.filter((producer) => producer.id !== id));
                    }

                    this.notificationService.showSuccessMessage("Successfully deleted", `Successfully deleted vehicle producer ${id}`);
                }
            },
            error: (error) => {
                this.notificationService.showErrorMessage("Error deleting vehicle producer", "Error");
            }
        });
    }

    getVehicleProducerById(id: number) {
        return this.httpClient.get<VehicleProducersModel>(`${this.url}/vehicle-producer/${id}`);
    }

    private handleError(err: any) {
        if (err.status === 400) {
            const errors = err?.error?.errors;
            if (errors && typeof errors === 'object') {
                Object.entries(errors).forEach(([field, message]) => {
                    if (field === 'correctVehicleProducerName') {
                        this.notificationService.showErrorMessage(`Correct Vehicle Producer Name: ${message}`, 'Error');
                    } else if (field === 'acceptedVehicleProducerName') {
                        this.notificationService.showErrorMessage(`Accepted Vehicle Producer Name: ${message}`, 'Error');
                    }
                });
            } else {
                this.notificationService.showErrorMessage("Invalid input data", "Validation Error");
            }
        } else if (err.status === 409) {
            const backendMessage = err?.error?.message;
            if (backendMessage) {
                this.notificationService.showErrorMessage(backendMessage, "Error");
            } else {
                this.notificationService.showErrorMessage("Vehicle producer already exists", "Error");
            }
        } else if (err.status === 404) {
            this.notificationService.showErrorMessage("Vehicle producer not found", "Not Found");
        } else {
            this.notificationService.showErrorMessage("Unexpected error occurred", "Error");
        }
    }
}
